import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { Box } from "./Box";
import { ButtonBase } from "./ButtonBase";
import { Collapse } from "./Collapse";
import { OverflowingTypography } from "./OverflowingTypography";
import { Stack } from "./Stack";
import { ExpandLessOutline, ExpandMoreOutline } from "../icons/icons";
import { ColorsProvider } from "../providers/ColorsProvider";
import { SlotsProvider } from "../providers/SlotsProvider";
import { useDefaultProps } from "../providers/utils/defaultProps";
import { mergeSx } from "../system";
import { reduceModifierBreakpoints } from "../system/utils/reduce-modifier-breakpoints";
export const accordionClasses = {
    root: "MinervaAccordion-root",
    accordion: "MinervaAccordion-accordion",
    label: "MinervaAccordion-label",
    "expand-icon": "MinervaAccordion-expandIcon",
    "expanded-icon": "MinervaAccordion-expandedIcon",
    collapse: "MinervaAccordion-collapse",
    "padding-wrapper": "MinervaAccordion-paddingWrapper",
    "label-container": "MinervaAccordion-labelContainer",
};
export const accordionSlots = {
    root: Stack,
    label: OverflowingTypography,
    expandIcon: ExpandMoreOutline,
    expandedIcon: ExpandLessOutline,
    collapse: Collapse,
    wrapper: Box,
};
const getPadding = (density) => density === "comfortable" ? 3 : density === "dense" ? 2 : 0;
export const Accordion = (props) => {
    const { label, hideExpandIcon, topDivider, bottomDivider, startNode, endNode, subNode, expanded: _expanded = false, onExpand, density = "comfortable", color = "background", colorSet, invertColors, slots, slotProps, sx, className, disabled, children, onClick, ...rest } = useDefaultProps({ component: "Accordion", props });
    const childRef = useRef(null);
    const [expanded, setExpanded] = useState(_expanded);
    useEffect(() => {
        setExpanded(_expanded ?? false);
    }, [_expanded]);
    return (_jsx(SlotsProvider, { baseSlots: accordionSlots, slots: slots, children: ({ root: Root, label: Label, expandIcon: ExpandIcon, expandedIcon: ExpandedIcon, collapse: Collapse, wrapper: Wrapper, }) => (_jsx(ColorsProvider, { color: disabled ? "disabled" : color, colorSet: colorSet, invertColors: disabled ? true : invertColors, children: (colorStyles) => (_jsxs(Root, { width: "100%", ...slotProps?.root, sx: mergeSx({
                    borderTop: topDivider ? 1 : 0,
                    borderBottom: bottomDivider ? 1 : 0,
                    borderColor: (theme) => theme.palette.stroke[100],
                }, slotProps?.root?.sx), children: [_jsxs(ButtonBase, { className: clsx([accordionClasses.root, className]), onClick: (e) => {
                            setExpanded(!expanded);
                            onExpand?.(!expanded);
                            onClick?.(e);
                        }, sx: mergeSx({
                            padding: getPadding(density),
                            color: colorStyles.color,
                            width: "100%",
                            textAlign: "left",
                            height: "fit-content",
                            display: "flex",
                            flexWrap: "nowrap",
                            alignItems: "center",
                            gap: 0.5,
                            breakpoints: density !== "comfortable" &&
                                density !== "dense" &&
                                density !== "none"
                                ? reduceModifierBreakpoints(density, (density) => ({
                                    padding: getPadding(density),
                                }))
                                : undefined,
                        }, sx), ...rest, children: [startNode, _jsxs(Stack, { className: accordionClasses["label-container"], sx: {
                                    gap: 0.5,
                                    flex: 1,
                                    minWidth: 0,
                                }, children: [typeof label === "string" ? (_jsx(Label, { variant: "body3", fontWeight: "semibold", ...slotProps?.label, className: clsx([
                                            accordionClasses.label,
                                            slotProps?.label?.className,
                                        ]), children: label })) : (label), subNode] }), endNode, !hideExpandIcon && (_jsxs(Box, { sx: {
                                    position: "relative",
                                }, children: [_jsx(ExpandedIcon, { ...slotProps?.expandedIcon, className: clsx([
                                            accordionClasses["expanded-icon"],
                                            slotProps?.expandedIcon?.className,
                                        ]), sx: mergeSx({
                                            position: "absolute",
                                            top: "50%",
                                            left: "50%",
                                            transform: expanded
                                                ? "translate(-50%, -50%) rotate(0deg)"
                                                : "translate(-50%, -50%) rotate(180deg)",
                                            opacity: expanded ? 1 : 0,
                                            transition: "transform 0.3s, opacity 0.3s",
                                        }, slotProps?.expandedIcon?.sx) }), _jsx(ExpandIcon, { ...slotProps?.expandIcon, className: clsx([
                                            accordionClasses["expand-icon"],
                                            slotProps?.expandIcon?.className,
                                        ]), sx: mergeSx({
                                            transform: !expanded
                                                ? "rotate(360deg)"
                                                : "rotate(180deg)",
                                            opacity: !expanded ? 1 : 0,
                                            transition: "transform 0.3s, opacity 0.3s",
                                        }, slotProps?.expandIcon?.sx) })] }))] }), _jsx(Collapse, { ref: childRef, expanded: expanded, ...slotProps?.collapse, className: clsx([
                            accordionClasses.collapse,
                            slotProps?.collapse?.className,
                        ]), children: _jsx(Wrapper, { ...slotProps?.wrapper, className: clsx([
                                accordionClasses["padding-wrapper"],
                                slotProps?.wrapper?.className,
                            ]), sx: mergeSx({
                                paddingBottom: getPadding(density),
                                paddingX: getPadding(density),
                                breakpoints: density !== "comfortable" &&
                                    density !== "dense" &&
                                    density !== "none"
                                    ? reduceModifierBreakpoints(density, (density) => {
                                        const padding = getPadding(density);
                                        return {
                                            [`&.${accordionClasses["padding-wrapper"]}`]: {
                                                paddingBottom: padding,
                                                paddingX: padding,
                                            },
                                        };
                                    })
                                    : undefined,
                            }, slotProps?.wrapper?.sx), children: children }) })] })) })) }));
};
//# sourceMappingURL=Accordion.js.map